import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';
import { generateImageForLesson } from './imageService';

// ========================================
// Types for Chinese Service
// ========================================

export interface ChineseWord {
  character: string;
  pinyin: string;
  english: string;
}

export interface ChineseLesson {
  lesson_id?: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  text: string;
  words: ChineseWord[];
  image_url?: string;
  image_prompt?: string;
  properties?: {
    vocabularyNotes?: { [character: string]: string };
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

export interface LessonSummary {
  lesson_id: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  created_at: string;
  updated_at: string;
}

export interface PracticeExercise {
  lesson_id: string;
  lesson_name?: string;
  lesson_objective?: string;
  englishText: string;
  difficulty: string;
  userTranslation?: string;
  evaluationResult?: {
    feedback: string;
    sentences: Array<{
      english: string;
      correct: string;
      pinyin: string;
      user: string;
      feedback: string;
    }>;
  };
  created_at: string;
  updated_at: string;
}

export interface WritingPractice {
  lesson_id: string;
  lesson_name?: string;
  lesson_objective?: string;
  topic: string;
  writingText: string;
  translations?: Array<{
    selectedText: string;
    pinyin: string;
    translation: string;
  }>;
  created_at: string;
  updated_at: string;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// Chinese Service Class
// ========================================

export class ChineseService {
  /**
   * Build system prompt for text generation only
   */
  private static buildTextPrompt(pastLessons?: LessonSummary[], customInstruction?: string): string {
    let prompt = `You are a Chinese language teacher creating reading passages for HSK 3-4 level students.

Your task is to generate a Chinese reading passage.

REQUIREMENTS:
- Create a passage of 800-1000 Chinese characters
- HSK 3-4 difficulty level (intermediate learners)
- Topics: daily life, Chinese culture, simple stories, travel, food, family, work, hobbies, etc.
- Include common grammatical structures for this level
- Make the content engaging, practical, and cohesive
- The passage should tell a complete story or describe a situation
- IMPORTANT: Use Chinese quotation marks (「」or『』) for dialogue, NOT English quotes (" or ')
- Avoid using any characters that require escaping in JSON (backslashes, quotes, etc.)`;

    if (pastLessons && pastLessons.length > 0) {
      const historyContext = pastLessons
        .map(l => `- "${l.lesson_name}": ${l.lesson_objective}`)
        .join('\n');
      
      prompt += `\n\nPREVIOUS LESSONS COVERED:\n${historyContext}\n\n`;
      prompt += `Generate a NEW lesson that builds upon or complements these topics. `;
      prompt += `Avoid repeating the same vocabulary and topics from previous lessons.\n`;
    }

    prompt += `\n\nRESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "lesson_name": "Short lesson title (e.g., 'Shopping at the Market')",
  "lesson_objective": "Brief description of what this lesson teaches (e.g., 'Learn vocabulary for shopping, prices, and bargaining')",
  "text": "Complete Chinese text passage (800-1000 characters)"
}

IMPORTANT:
- Focus on creating natural, flowing Chinese text
- Use varied vocabulary and sentence structures
- Make sure the JSON is valid and properly formatted
- The text should be interesting and educational
- Use Chinese quotation marks 「」or『』for any dialogue or quoted speech`;

    return prompt;
  }

  /**
   * Build system prompt for vocabulary extraction
   */
  private static buildVocabularyPrompt(text: string): string {
    return `You are a Chinese language teacher analyzing a text for HSK 3-4 level students.

Your task is to extract and translate key vocabulary from the provided Chinese text.

CHINESE TEXT:
${text}

REQUIREMENTS:
- Extract important words and phrases that students should learn
- Include common words, useful phrases, and key vocabulary
- Provide accurate pinyin with proper tone marks (ā, á, ǎ, à, ē, é, ě, è, etc.)
- Provide clear English translations
- For multi-character words, keep them together (e.g., "中国" not "中" and "国" separately)
- Focus on words that are useful for HSK 3-4 level learners
- Include approximately 30-50 vocabulary items

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "words": [
    {
      "character": "Chinese word or phrase",
      "pinyin": "pinyin with proper tone marks",
      "english": "English translation"
    }
  ]
}

IMPORTANT:
- Use proper pinyin tone marks (māma, not mama)
- Order words as they appear in the text
- Make sure the JSON is valid and properly formatted`;
  }

  /**
   * Generate Chinese text only (Step 1)
   */
  private static async generateText(
    pastLessons?: LessonSummary[], 
    customInstruction?: string
  ): Promise<{ lesson_name: string; lesson_objective: string; text: string }> {
    try {
      console.log('🇨🇳 [ChineseService] Step 1: Generating Chinese text...');

      const systemPrompt = this.buildTextPrompt(pastLessons, customInstruction);
      
      let userMessage = 'Generate a new Chinese reading passage following the requirements.';
      if (customInstruction) {
        userMessage = `Generate a new Chinese reading passage following the requirements. Focus the lesson on: ${customInstruction}`;
      }
      
      const messages = [
        {
          role: 'user' as const,
          content: userMessage
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 8000, // Increased for longer responses
        temperature: 0.8
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate Chinese text');
      }

      // Check if response was truncated due to max_tokens
      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [ChineseService] AI text response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. Please try again.');
      }

      // Extract the AI response
      // Try to use the structured content first, then fall back to response string
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for text generation');
      }

      console.log('🇨🇳 [ChineseService] Raw AI response length:', aiResponse.length);
      console.log('🇨🇳 [ChineseService] First 200 chars:', aiResponse.substring(0, 200));

      // Parse JSON from the response
      // Handle various markdown code block formats
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      console.log('🇨🇳 [ChineseService] After markdown removal, length:', jsonString.length);
      console.log('🇨🇳 [ChineseService] First 300 chars:', jsonString.substring(0, 300));
      
      // Check if JSON appears to be truncated (incomplete)
      if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
        console.error('🇨🇳 [ChineseService] JSON appears to be truncated');
        throw new Error('AI response was incomplete (truncated JSON). Please try again.');
      }
      
      let textData;
      try {
        textData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for text:', parseError);
        console.error('🇨🇳 [ChineseService] Character at error position:');
        if (parseError instanceof SyntaxError) {
          const match = parseError.message.match(/position (\d+)/);
          if (match) {
            const pos = parseInt(match[1]);
            console.error('Context around position', pos, ':', jsonString.substring(Math.max(0, pos - 50), Math.min(jsonString.length, pos + 50)));
            console.error('Character codes:', Array.from(jsonString.substring(Math.max(0, pos - 5), Math.min(jsonString.length, pos + 5))).map(c => c.charCodeAt(0)));
          }
        }
        
        // If it's a truncation error, provide better user feedback
        if (parseError instanceof Error && 
            (parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. Please try again.');
        }
        
        throw new Error(`Failed to parse text JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!textData || typeof textData !== 'object') {
        throw new Error('Invalid JSON response - expected an object');
      }
      
      if (!textData.lesson_name || !textData.lesson_objective || !textData.text) {
        throw new Error('Invalid text structure - missing lesson_name, lesson_objective, or text');
      }
      
      if (typeof textData.lesson_name !== 'string' || 
          typeof textData.lesson_objective !== 'string' || 
          typeof textData.text !== 'string') {
        throw new Error('Invalid text structure - lesson_name, lesson_objective, and text must be strings');
      }

      // JSON.parse automatically handles \n and \" escape sequences
      // The text is already properly formatted after parsing
      const cleanedText = textData.text;

      console.log('🇨🇳 [ChineseService] Text generated successfully:', {
        lesson_name: textData.lesson_name,
        lesson_objective: textData.lesson_objective,
        textLength: cleanedText.length,
        textPreview: cleanedText.substring(0, 100) + '...'
      });

      return {
        lesson_name: textData.lesson_name,
        lesson_objective: textData.lesson_objective,
        text: cleanedText
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error generating text:', error);
      throw error;
    }
  }

  /**
   * Generate vocabulary from Chinese text (Step 2)
   */
  private static async generateVocabulary(text: string): Promise<ChineseWord[]> {
    try {
      console.log('🇨🇳 [ChineseService] Step 2: Generating vocabulary...');

      const systemPrompt = this.buildVocabularyPrompt(text);
      
      const messages = [
        {
          role: 'user' as const,
          content: 'Extract and translate the key vocabulary from this text following the requirements.'
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 8000, // Increased for vocabulary responses
        temperature: 0.3 // Lower temperature for more accurate translations
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate vocabulary');
      }

      // Check if response was truncated due to max_tokens
      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [ChineseService] AI vocabulary response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. Please try again.');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract vocabulary response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for vocabulary generation');
      }

      // Parse JSON from the response
      // Handle various markdown code block formats
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      // Check if JSON appears to be truncated (incomplete)
      if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
        console.error('🇨🇳 [ChineseService] JSON appears to be truncated');
        throw new Error('AI response was incomplete (truncated JSON). Please try again.');
      }
      
      let vocabData;
      try {
        vocabData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for vocabulary:', parseError);
        console.error('🇨🇳 [ChineseService] Raw AI response:', aiResponse.substring(0, 200));
        console.error('🇨🇳 [ChineseService] Attempted to parse:', jsonString.substring(0, 500));
        
        // If it's a truncation error, provide better user feedback
        if (parseError instanceof Error && 
            (parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. Please try again.');
        }
        
        throw new Error(`Failed to parse vocabulary JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!vocabData || typeof vocabData !== 'object') {
        throw new Error('Invalid vocabulary JSON response - expected an object');
      }
      
      if (!Array.isArray(vocabData.words)) {
        throw new Error('Invalid vocabulary structure - words must be an array');
      }
      
      // Validate each word has required fields
      const validWords = vocabData.words.filter((word: any) => {
        return word && 
               typeof word.character === 'string' && 
               typeof word.pinyin === 'string' && 
               typeof word.english === 'string';
      });
      
      if (validWords.length === 0) {
        throw new Error('No valid vocabulary words found');
      }

      console.log('🇨🇳 [ChineseService] Vocabulary generated successfully:', {
        totalWords: vocabData.words.length,
        validWords: validWords.length
      });

      return validWords;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error generating vocabulary:', error);
      throw error;
    }
  }

  /**
   * Generate a new Chinese lesson from recent news using Gemini with search grounding
   * @param newsTopics Optional topics to focus on in news search
   * @param selectedLessonIds Array of lesson IDs to include as context
   * @param onProgress Optional callback to track progress: 'searching' | 'text' | 'vocabulary'
   */
  public static async generateLessonFromNews(
    newsTopics?: string,
    selectedLessonIds: string[] = [],
    onProgress?: (stage: 'searching' | 'text' | 'vocabulary') => void
  ): Promise<ChineseLesson> {
    try {
      console.log('🌐 [ChineseService] Generating news-based Chinese lesson...');

      // Step 1: Search for news and generate text
      if (onProgress) onProgress('searching');

      const result = await apiCall('/lessons/generate-from-news', {
        method: 'POST',
        body: JSON.stringify({
          category: 'chinese',
          newsTopics: newsTopics || undefined,
          selectedLessonIds: selectedLessonIds
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate news-based lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: ChineseLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        properties: data.properties || null,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      console.log('🌐 [ChineseService] News-based lesson generated successfully:', {
        lesson_name: lesson.lesson_name,
        textLength: lesson.text.length,
        wordCount: lesson.words.length
      });

      return lesson;
    } catch (error) {
      console.error('🌐 [ChineseService] Error generating news-based lesson:', error);
      
      // Handle authentication errors
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate Chinese lessons.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Generate a new Chinese lesson using AI and save to database
   * @param selectedLessonIds Array of lesson IDs to include as context
   * @param customInstruction Optional custom topic instruction
   * @param onProgress Optional callback to track progress: 'text' | 'vocabulary'
   */
  public static async generateLesson(
    selectedLessonIds: string[] = [],
    customInstruction?: string,
    onProgress?: (stage: 'text' | 'vocabulary') => void
  ): Promise<ChineseLesson> {
    try {
      console.log('🇨🇳 [ChineseService] Generating new Chinese lesson...');

      // Fetch selected past lessons for context
      let pastLessons: LessonSummary[] = [];
      if (selectedLessonIds.length > 0) {
        console.log('🇨🇳 [ChineseService] Fetching past lessons for context...');
        const allLessons = await this.getPastLessons(100); // Get up to 100 lessons
        pastLessons = allLessons.lessons.filter(l => selectedLessonIds.includes(l.lesson_id));
      }

      // Step 1: Generate the Chinese text
      if (onProgress) onProgress('text');
      const { lesson_name, lesson_objective, text } = await this.generateText(pastLessons, customInstruction);

      // Step 2: Generate vocabulary from the text
      if (onProgress) onProgress('vocabulary');
      const words = await this.generateVocabulary(text);

      console.log('🇨🇳 [ChineseService] Lesson generated successfully:', {
        lesson_name,
        lesson_objective,
        textLength: text.length,
        wordCount: words.length
      });

      // Step 3: Save lesson to database
      const savedLesson = await this.saveLesson({
        lesson_name,
        lesson_objective,
        category: 'chinese',
        text,
        words
      });

      return savedLesson;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error generating lesson:', error);
      
      // Handle authentication errors
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate Chinese lessons.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Save lesson to database
   */
  public static async saveLesson(lessonData: {
    lesson_name: string;
    lesson_objective: string;
    category: string;
    text: string;
    words: ChineseWord[];
  }): Promise<ChineseLesson> {
    try {
      const result = await apiCall('/lessons', {
        method: 'POST',
        body: JSON.stringify({
          category: lessonData.category,
          lesson_name: lessonData.lesson_name,
          lesson_objective: lessonData.lesson_objective,
          lesson_content: {
            text: lessonData.text,
            vocabulary: lessonData.words,
            grammar: null
          }
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to save lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const savedLesson: ChineseLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        properties: data.properties || null,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return savedLesson;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error saving lesson:', error);
      throw error;
    }
  }

  /**
   * Get past lessons for user (category: chinese)
   */
  public static async getPastLessons(limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=chinese&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lessons');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      return {
        lessons: data.lessons || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error fetching past lessons:', error);
      throw error;
    }
  }

  /**
   * Get specific lesson by ID
   */
  public static async getLessonById(lessonId: string): Promise<ChineseLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: ChineseLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        image_url: data.image_url,
        image_prompt: data.image_prompt,
        properties: data.properties || null,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error fetching lesson:', error);
      throw error;
    }
  }

  /**
   * Generate image for a lesson using AI
   */
  public static async generateLessonImage(
    lessonId: string,
    prompt: string,
    provider?: 'gemini' | 'grok'
  ): Promise<{ image: string; mimeType: string }> {
    try {
      return await generateImageForLesson(lessonId, prompt, provider);
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error generating image:', error);
      throw error;
    }
  }

  /**
   * Upload image to Firebase Storage and save URL to lesson
   */
  public static async uploadLessonImage(
    lessonId: string,
    imageData: string,
    mimeType: string,
    prompt: string
  ): Promise<string> {
    try {
      // Convert base64 to blob
      const base64Response = await fetch(`data:${mimeType};base64,${imageData}`);
      const blob = await base64Response.blob();

      // Upload to Firebase Storage
      const { storage } = await import('../firebase/config');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const extension = mimeType.split('/')[1] || 'png';
      const fileName = `${timestamp}_${randomId}.${extension}`;
      const storagePath = `lessons/${lessonId}/images/${fileName}`;

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: mimeType });

      // Get public URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update lesson with image URL and prompt
      await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          image_url: downloadURL,
          image_prompt: prompt
        })
      });

      return downloadURL;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Update lesson name
   */
  public static async updateLessonName(lessonId: string, newName: string): Promise<ChineseLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ lesson_name: newName })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: ChineseLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        properties: data.properties || null,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error updating lesson:', error);
      throw error;
    }
  }

  /**
   * Update lesson properties
   */
  public static async updateLessonProperties(lessonId: string, properties: any): Promise<ChineseLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ properties })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update lesson properties');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: ChineseLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        image_url: data.image_url,
        image_prompt: data.image_prompt,
        properties: data.properties || null,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error updating lesson properties:', error);
      throw error;
    }
  }

  /**
   * Delete lesson
   */
  public static async deleteLesson(lessonId: string): Promise<void> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete lesson');
      }
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error deleting lesson:', error);
      throw error;
    }
  }

  /**
   * Generate a practice exercise (English paragraph) for translation practice
   * @param difficulty Difficulty level: 'HSK 3-4', 'Beginner', 'Intermediate', or 'Advanced'
   */
  public static async generatePracticeExercise(difficulty: string): Promise<{ englishText: string; difficulty: string }> {
    try {
      console.log('🇨🇳 [ChineseService] Generating practice exercise...');

      const difficultyPrompts: { [key: string]: string } = {
        'HSK 3-4': 'HSK 3-4 level (intermediate learners). Use vocabulary and grammar structures appropriate for HSK 3-4. Paragraph should be 3-5 sentences, 50-100 words total.',
        'Beginner': 'Beginner level. Use simple vocabulary and basic sentence structures. Paragraph should be 2-3 sentences, 30-50 words total.',
        'Intermediate': 'Intermediate level. Use varied vocabulary and more complex sentence structures. Paragraph should be 4-6 sentences, 80-120 words total.',
        'Advanced': 'Advanced level. Use sophisticated vocabulary and complex grammatical structures. Paragraph should be 5-8 sentences, 120-200 words total.'
      };

      const difficultyDescription = difficultyPrompts[difficulty] || difficultyPrompts['HSK 3-4'];

      // Random topics to ensure variety
      const randomTopics = [
        'morning routine', 'shopping at a market', 'ordering food at a restaurant', 'asking for directions',
        'talking about hobbies', 'describing the weather', 'discussing weekend plans', 'sharing travel experiences',
        'explaining a recipe', 'describing a friend', 'talking about studies', 'discussing work',
        'shopping for clothes', 'visiting a museum', 'attending a concert', 'celebrating a festival',
        'going to the library', 'exercising at the gym', 'cooking dinner', 'watching a movie',
        'reading a book', 'learning a new skill', 'planning a trip', 'describing a city',
        'talking about family', 'discussing health', 'sharing memories', 'explaining traditions',
        'describing seasons', 'talking about technology', 'discussing education', 'sharing opinions'
      ];

      // Pick a random topic
      const randomTopic = randomTopics[Math.floor(Math.random() * randomTopics.length)];

      // Add random variation elements
      const randomVariations = [
        'with specific details and examples',
        'using vivid descriptions',
        'with a personal perspective',
        'including dialogue or conversation',
        'with cultural context',
        'focusing on actions and activities',
        'describing feelings and emotions',
        'with time references and sequencing'
      ];
      const randomVariation = randomVariations[Math.floor(Math.random() * randomVariations.length)];

      // Add timestamp for additional randomness
      const timestamp = Date.now();
      const randomSeed = Math.floor(Math.random() * 10000);

      const systemPrompt = `You are a Chinese language teacher creating translation practice exercises.

Your task is to generate a COMPLETELY UNIQUE and RANDOM English paragraph suitable for Chinese translation practice.

DIFFICULTY LEVEL: ${difficultyDescription}

CRITICAL RANDOMIZATION REQUIREMENTS:
- Generate a UNIQUE paragraph that is DIFFERENT from any previous exercises
- Vary the topic, vocabulary, sentence structure, and content style significantly
- Use CREATIVE and DIVERSE approaches - avoid formulaic or repetitive patterns
- Each paragraph should feel fresh and original

TOPIC FOCUS: ${randomTopic}
STYLE VARIATION: ${randomVariation}

REQUIREMENTS:
- Generate a natural, coherent English paragraph
- Make sure the paragraph is suitable for translation practice at the specified difficulty level
- The paragraph should be self-contained and make sense on its own
- Use varied sentence structures and vocabulary
- Be creative and avoid repetition of common phrases or patterns

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "englishText": "The complete English paragraph here"
}

IMPORTANT:
- Return ONLY valid JSON, no markdown code blocks
- The englishText should be a single paragraph
- Make sure the JSON is properly formatted
- Ensure the content is UNIQUE and DIFFERENT from typical exercises`;

      const messages = [
        {
          role: 'user' as const,
          content: `Generate a completely unique and random English paragraph for ${difficulty} level Chinese translation practice. Focus on the topic: ${randomTopic}. Make it ${randomVariation}. Ensure it's creative and different from typical exercises. (Seed: ${timestamp}-${randomSeed})`
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 2000,
        temperature: 1.0 // Increased temperature for more randomness
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate practice exercise');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract exercise response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for exercise generation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      let exerciseData;
      try {
        exerciseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for exercise:', parseError);
        throw new Error(`Failed to parse exercise JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!exerciseData || typeof exerciseData !== 'object') {
        throw new Error('Invalid exercise JSON response - expected an object');
      }
      
      if (!exerciseData.englishText || typeof exerciseData.englishText !== 'string') {
        throw new Error('Invalid exercise structure - englishText must be a string');
      }

      console.log('🇨🇳 [ChineseService] Practice exercise generated successfully');

      return {
        englishText: exerciseData.englishText,
        difficulty: difficulty
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error generating practice exercise:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate practice exercises.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Generate the correct Chinese translation for an English text using LLM
   * @param englishText The English text to translate
   * @param difficulty The difficulty level
   */
  public static async generateAnswer(
    englishText: string,
    difficulty: string
  ): Promise<{ translation: string; pinyin: string }> {
    try {
      console.log('🇨🇳 [ChineseService] Generating answer translation...');

      const difficultyPrompts: { [key: string]: string } = {
        'HSK 3-4': 'HSK 3-4 level (intermediate learners). Use vocabulary and grammar structures appropriate for HSK 3-4.',
        'Beginner': 'Beginner level. Use simple vocabulary and basic sentence structures.',
        'Intermediate': 'Intermediate level. Use varied vocabulary and more complex sentence structures.',
        'Advanced': 'Advanced level. Use sophisticated vocabulary and complex grammatical structures.'
      };

      const difficultyDescription = difficultyPrompts[difficulty] || difficultyPrompts['HSK 3-4'];

      const systemPrompt = `You are a Chinese language teacher providing accurate translations from English to Chinese.

Your task is to translate the provided English text into natural, fluent Chinese.

DIFFICULTY LEVEL: ${difficultyDescription}

REQUIREMENTS:
- Provide an accurate and natural Chinese translation
- Use appropriate vocabulary and grammar for the difficulty level
- Ensure the translation sounds natural and fluent in Chinese
- Maintain the meaning and tone of the original English text
- Provide accurate pinyin with proper tone marks for the translation

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "translation": "Complete Chinese translation",
  "pinyin": "Pinyin with tone marks for the entire translation"
}

IMPORTANT:
- The translation should be natural and fluent Chinese
- Use proper Chinese punctuation
- Provide accurate pinyin with tone marks (e.g., nǐ hǎo, wǒ shì)
- Return ONLY valid JSON, no markdown code blocks`;

      const messages = [
        {
          role: 'user' as const,
          content: `Translate this English text to Chinese:

${englishText}

Provide a natural, fluent Chinese translation appropriate for ${difficulty} level.`
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 2000,
        temperature: 0.3 // Lower temperature for more consistent translations
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate answer');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract answer response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for answer generation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      let answerData;
      try {
        answerData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for answer:', parseError);
        throw new Error(`Failed to parse answer JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!answerData || typeof answerData !== 'object') {
        throw new Error('Invalid answer JSON response - expected an object');
      }
      
      if (!answerData.translation || typeof answerData.translation !== 'string') {
        throw new Error('Invalid answer structure - translation must be a string');
      }

      if (!answerData.pinyin || typeof answerData.pinyin !== 'string') {
        throw new Error('Invalid answer structure - pinyin must be a string');
      }

      console.log('🇨🇳 [ChineseService] Answer generated successfully');

      return {
        translation: answerData.translation,
        pinyin: answerData.pinyin
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error generating answer:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate answers.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Generate a preview text from English text for use in titles/objectives
   */
  private static generateTextPreview(text: string, maxLength: number = 60): string {
    // Clean the text: remove extra whitespace, newlines, etc.
    const cleaned = text.trim().replace(/\s+/g, ' ');
    
    // Take first sentence or first maxLength characters
    const firstSentenceEnd = cleaned.match(/[.!?]/);
    if (firstSentenceEnd && firstSentenceEnd.index! < maxLength) {
      return cleaned.substring(0, firstSentenceEnd.index! + 1).trim();
    }
    
    // If no sentence ending found or it's too long, truncate at word boundary
    if (cleaned.length <= maxLength) {
      return cleaned;
    }
    
    const truncated = cleaned.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > maxLength * 0.7) {
      return truncated.substring(0, lastSpace) + '...';
    }
    return truncated + '...';
  }

  /**
   * Save practice exercise to database
   * @param englishText The English text to translate
   * @param difficulty The difficulty level
   * @param userTranslation Optional user translation
   * @param evaluationResult Optional evaluation result
   * @param lessonId Optional lesson ID for updating existing exercise
   */
  public static async savePracticeExercise(
    englishText: string,
    difficulty: string,
    userTranslation?: string,
    evaluationResult?: { feedback: string; sentences: Array<{ english: string; correct: string; pinyin: string; user: string; feedback: string }> },
    lessonId?: string
  ): Promise<{ lesson_id: string }> {
    try {
      const { makeAuthenticatedRequest } = await import('./authService');
      
      const lessonContent = {
        englishText,
        difficulty,
        practiceType: 'translation',
        ...(userTranslation && { userTranslation }),
        ...(evaluationResult && { evaluationResult })
      };

      // Generate descriptive title and objective with preview of English text
      const textPreview = this.generateTextPreview(englishText, 50);
      const lessonName = `Translation Practice - ${difficulty} - ${textPreview}`;
      const lessonObjective = `${textPreview} (${difficulty} level translation practice)`;

      const lessonData = {
        category: 'chinese_practice_translation',
        lesson_name: lessonName,
        lesson_objective: lessonObjective,
        lesson_content: lessonContent
      };

      let result;
      if (lessonId) {
        // Update existing lesson
        result = await makeAuthenticatedRequest(`/lessons/${lessonId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            lesson_name: lessonName,
            lesson_objective: lessonObjective,
            lesson_content: lessonContent
          })
        });
      } else {
        // Create new lesson
        result = await makeAuthenticatedRequest('/lessons', {
          method: 'POST',
          body: JSON.stringify(lessonData)
        });
      }

      if (!result.success) {
        throw new Error(result.message || 'Failed to save practice exercise');
      }

      const data = result.data || result;
      return { lesson_id: data.lesson_id || lessonId || '' };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error saving practice exercise:', error);
      throw error;
    }
  }

  /**
   * Evaluate user's Chinese translation of an English paragraph
   * @param englishText The original English text
   * @param userTranslation The user's Chinese translation
   * @param difficulty The difficulty level of the exercise
   */
  public static async evaluateTranslation(
    englishText: string,
    userTranslation: string,
    difficulty: string
  ): Promise<{ feedback: string; sentences: Array<{ english: string; correct: string; pinyin: string; user: string; feedback: string }> }> {
    try {
      console.log('🇨🇳 [ChineseService] Evaluating translation...');

      const systemPrompt = `You are a Chinese language teacher evaluating a student's translation from English to Chinese.

Your task is to provide detailed, constructive feedback on the translation.

EVALUATION CRITERIA:
1. Accuracy: Does the translation convey the correct meaning?
2. Grammar: Are the Chinese grammar structures correct?
3. Vocabulary: Are the word choices appropriate?
4. Naturalness: Does the Chinese sound natural and fluent?
5. Pinyin: Provide accurate pinyin with tone marks for the correct translation.

FEEDBACK FORMAT:
- Provide an overall assessment of the translation
- Break down the evaluation sentence-by-sentence
- For each sentence, identify what is correct and what needs improvement
- Be encouraging but honest about mistakes
- Suggest corrections when appropriate

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "feedback": "Overall assessment of the translation (2-3 sentences)",
  "sentences": [
    {
      "english": "Original English sentence",
      "correct": "Correct Chinese translation",
      "pinyin": "Pinyin with tone marks for the correct translation",
      "user": "User's Chinese translation",
      "feedback": "Detailed feedback on what's correct and what needs improvement"
    }
  ]
}

IMPORTANT:
- Split the English text into sentences for analysis
- Provide accurate Chinese translations for each sentence
- Provide accurate pinyin with tone marks (e.g., nǐ hǎo) for the correct translation
- Compare user's translation with the correct translation
- Give specific, actionable feedback
- Return ONLY valid JSON, no markdown code blocks`;

      const messages = [
        {
          role: 'user' as const,
          content: `Evaluate this translation:

ORIGINAL ENGLISH TEXT:
${englishText}

USER'S CHINESE TRANSLATION:
${userTranslation}

DIFFICULTY LEVEL: ${difficulty}

Please provide sentence-by-sentence feedback on this translation.`
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 4000,
        temperature: 0.3 // Lower temperature for more consistent evaluation
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to evaluate translation');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract evaluation response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for translation evaluation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      let evaluationData;
      try {
        evaluationData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for evaluation:', parseError);
        throw new Error(`Failed to parse evaluation JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!evaluationData || typeof evaluationData !== 'object') {
        throw new Error('Invalid evaluation JSON response - expected an object');
      }
      
      if (!evaluationData.feedback || typeof evaluationData.feedback !== 'string') {
        throw new Error('Invalid evaluation structure - feedback must be a string');
      }
      
      if (!Array.isArray(evaluationData.sentences)) {
        throw new Error('Invalid evaluation structure - sentences must be an array');
      }

      // Validate sentence structure
      const validSentences = evaluationData.sentences.filter((s: any) => {
        return s && 
               typeof s.english === 'string' && 
               typeof s.correct === 'string' && 
               typeof s.pinyin === 'string' &&
               typeof s.user === 'string' && 
               typeof s.feedback === 'string';
      });

      if (validSentences.length === 0) {
        throw new Error('No valid sentence feedback found in evaluation');
      }

      console.log('🇨🇳 [ChineseService] Translation evaluated successfully:', {
        sentenceCount: validSentences.length
      });

      return {
        feedback: evaluationData.feedback,
        sentences: validSentences
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error evaluating translation:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to evaluate translations.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Get past practice exercises for user (category: chinese_practice_translation)
   */
  public static async getPastPracticeExercises(limit: number = 50, offset: number = 0): Promise<{
    exercises: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=chinese_practice_translation&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch practice exercises');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      return {
        exercises: data.lessons || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error fetching past practice exercises:', error);
      throw error;
    }
  }

  /**
   * Get specific practice exercise by ID
   */
  public static async getPracticeExerciseById(lessonId: string): Promise<PracticeExercise> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch practice exercise');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Validate that this is a practice exercise
      if (data.category !== 'chinese_practice_translation') {
        throw new Error('Lesson is not a practice exercise');
      }

      // Extract practice exercise data from lesson_content
      const lessonContent = data.lesson_content || {};
      
      const exercise: PracticeExercise = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        englishText: lessonContent.englishText || '',
        difficulty: lessonContent.difficulty || '',
        userTranslation: lessonContent.userTranslation,
        evaluationResult: lessonContent.evaluationResult,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return exercise;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error fetching practice exercise:', error);
      throw error;
    }
  }

  /**
   * Continue writing in Chinese with AI assistance
   * @param topic The writing topic
   * @param currentText The current text written by the user
   * @param intention Optional user intention for what the next sentence should focus on
   */
  public static async continueWriting(
    topic: string,
    currentText: string,
    intention?: string
  ): Promise<{ nextSentence: string }> {
    try {
      console.log('🇨🇳 [ChineseService] Continuing writing...');

      const systemPrompt = `You are a Chinese writing assistant helping a student write in Chinese.

Your task is to continue the user's Chinese writing by generating ONE natural next sentence that flows seamlessly from their existing text.

TOPIC: ${topic}

REQUIREMENTS:
- Generate ONE complete sentence in Chinese that naturally continues the writing
- Match the writing style, tone, and difficulty level of the existing text
- If the existing text is empty, start with an appropriate opening sentence for the topic
- Use natural Chinese sentence structures and appropriate vocabulary
- Ensure the sentence flows logically from what was written before
- Use proper Chinese punctuation (。，！？)
- The sentence should be complete and grammatically correct
- Keep the sentence length appropriate (not too short, not too long)
- If the user provides an intention, use it to guide the direction and focus of the next sentence

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "nextSentence": "The next sentence in Chinese"
}

IMPORTANT:
- Return ONLY valid JSON, no markdown code blocks
- The nextSentence should be a single, complete Chinese sentence
- Do not include the existing text in your response, only the new sentence
- Make sure the JSON is properly formatted`;

      let userMessage = currentText.trim()
        ? `Continue writing about "${topic}". Here is what has been written so far:\n\n${currentText}\n\nGenerate the next sentence that naturally continues this writing.`
        : `Start writing about "${topic}". Generate an appropriate opening sentence in Chinese.`;
      
      if (intention) {
        userMessage += `\n\nUser intention: ${intention}`;
      }

      const messages = [
        {
          role: 'user' as const,
          content: userMessage
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 500,
        temperature: 0.7
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to continue writing');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract writing continuation response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for writing continuation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      let writingData;
      try {
        writingData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for writing continuation:', parseError);
        throw new Error(`Failed to parse writing continuation JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!writingData || typeof writingData !== 'object') {
        throw new Error('Invalid writing continuation JSON response - expected an object');
      }
      
      if (!writingData.nextSentence || typeof writingData.nextSentence !== 'string') {
        throw new Error('Invalid writing continuation structure - nextSentence must be a string');
      }

      console.log('🇨🇳 [ChineseService] Writing continuation generated successfully');

      return {
        nextSentence: writingData.nextSentence
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error continuing writing:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to use Writing Copilot.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Translate Chinese phrase to English with pinyin
   * @param chineseText The Chinese text to translate
   */
  public static async translatePhrase(
    chineseText: string
  ): Promise<{ pinyin: string; translation: string }> {
    try {
      console.log('🇨🇳 [ChineseService] Translating phrase...');

      const systemPrompt = `You are a Chinese language teacher providing accurate translations from Chinese to English.

Your task is to translate the provided Chinese text into natural English and provide accurate pinyin with proper tone marks.

REQUIREMENTS:
- Provide an accurate and natural English translation
- Provide accurate pinyin with proper tone marks (ā, á, ǎ, à, ē, é, ě, è, etc.)
- Ensure the translation sounds natural and fluent in English
- Maintain the meaning and tone of the original Chinese text
- For phrases or sentences, provide complete pinyin for the entire text
- Use proper spacing in pinyin between words

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "pinyin": "Pinyin with tone marks for the entire Chinese text",
  "translation": "Natural English translation"
}

IMPORTANT:
- Use proper pinyin tone marks (e.g., nǐ hǎo, wǒ shì)
- Return ONLY valid JSON, no markdown code blocks
- The translation should be natural and fluent English
- Make sure the JSON is properly formatted`;

      const messages = [
        {
          role: 'user' as const,
          content: `Translate this Chinese text to English and provide pinyin:\n\n${chineseText}`
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 1000,
        temperature: 0.3
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to translate phrase');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇨🇳 [ChineseService] Failed to extract translation response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for translation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      let translationData;
      try {
        translationData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇨🇳 [ChineseService] JSON parse error for translation:', parseError);
        throw new Error(`Failed to parse translation JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!translationData || typeof translationData !== 'object') {
        throw new Error('Invalid translation JSON response - expected an object');
      }
      
      if (!translationData.pinyin || typeof translationData.pinyin !== 'string') {
        throw new Error('Invalid translation structure - pinyin must be a string');
      }

      if (!translationData.translation || typeof translationData.translation !== 'string') {
        throw new Error('Invalid translation structure - translation must be a string');
      }

      console.log('🇨🇳 [ChineseService] Translation generated successfully');

      return {
        pinyin: translationData.pinyin,
        translation: translationData.translation
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error translating phrase:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to use Translation.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Generate a preview text from Chinese text for use in titles/objectives
   */
  private static generateChineseTextPreview(text: string, maxLength: number = 60): string {
    // Clean the text: remove extra whitespace, newlines, etc.
    const cleaned = text.trim().replace(/\s+/g, '');
    
    // Take first sentence or first maxLength characters
    const firstSentenceEnd = cleaned.match(/[。！？]/);
    if (firstSentenceEnd && firstSentenceEnd.index! < maxLength) {
      return cleaned.substring(0, firstSentenceEnd.index! + 1).trim();
    }
    
    // If no sentence ending found or it's too long, truncate
    if (cleaned.length <= maxLength) {
      return cleaned;
    }
    
    return cleaned.substring(0, maxLength) + '...';
  }

  /**
   * Save writing practice to database
   * @param topic The writing topic
   * @param writingText The Chinese text written
   * @param translations Optional array of translations made
   * @param lessonId Optional lesson ID for updating existing practice
   */
  public static async saveWritingPractice(
    topic: string,
    writingText: string,
    translations?: Array<{
      selectedText: string;
      pinyin: string;
      translation: string;
    }>,
    lessonId?: string
  ): Promise<{ lesson_id: string }> {
    try {
      const { makeAuthenticatedRequest } = await import('./authService');
      
      const lessonContent = {
        topic,
        writingText,
        practiceType: 'writing',
        ...(translations && translations.length > 0 && { translations })
      };

      // Generate descriptive title and objective with preview of text
      const textPreview = this.generateChineseTextPreview(writingText, 50);
      const lessonName = `Writing Practice - ${topic}`;
      const lessonObjective = textPreview 
        ? `${textPreview} (Topic: ${topic})`
        : `Writing practice on ${topic}`;

      const lessonData = {
        category: 'chinese_practice_writing',
        lesson_name: lessonName,
        lesson_objective: lessonObjective,
        lesson_content: lessonContent
      };

      let result;
      if (lessonId) {
        // Update existing lesson
        result = await makeAuthenticatedRequest(`/lessons/${lessonId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            lesson_name: lessonName,
            lesson_objective: lessonObjective,
            lesson_content: lessonContent
          })
        });
      } else {
        // Create new lesson
        result = await makeAuthenticatedRequest('/lessons', {
          method: 'POST',
          body: JSON.stringify(lessonData)
        });
      }

      if (!result.success) {
        throw new Error(result.message || 'Failed to save writing practice');
      }

      const data = result.data || result;
      return { lesson_id: data.lesson_id || lessonId || '' };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error saving writing practice:', error);
      throw error;
    }
  }

  /**
   * Get past writing practices for user (category: chinese_practice_writing)
   */
  public static async getPastWritingPractices(limit: number = 50, offset: number = 0): Promise<{
    writings: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=chinese_practice_writing&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch writing practices');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      return {
        writings: data.lessons || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error fetching past writing practices:', error);
      throw error;
    }
  }

  /**
   * Get specific writing practice by ID
   */
  public static async getWritingPracticeById(lessonId: string): Promise<WritingPractice> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch writing practice');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Validate that this is a writing practice
      if (data.category !== 'chinese_practice_writing') {
        throw new Error('Lesson is not a writing practice');
      }

      // Extract writing practice data from lesson_content
      const lessonContent = data.lesson_content || {};
      
      const writing: WritingPractice = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        topic: lessonContent.topic || '',
        writingText: lessonContent.writingText || '',
        translations: lessonContent.translations,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return writing;
    } catch (error) {
      console.error('🇨🇳 [ChineseService] Error fetching writing practice:', error);
      throw error;
    }
  }
}

